import React, { useState, useEffect } from 'react';
import { Mail, Trash2, Check, X, ArrowLeft, Search, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { useToast } from '../hooks/use-toast';
import { getContacts, markContactAsRead, deleteContact } from '../services/api';
import { useNavigate } from 'react-router-dom';

const Admin = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUnread, setFilterUnread] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await getContacts();
      setContacts(response.data || []);
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao carregar mensagens",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      await markContactAsRead(id);
      setContacts(contacts.map(c => 
        c.id === id ? { ...c, read: true } : c
      ));
      if (selectedContact?.id === id) {
        setSelectedContact({ ...selectedContact, read: true });
      }
      toast({
        title: "Sucesso",
        description: "Mensagem marcada como lida",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao marcar mensagem como lida",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar esta mensagem?')) {
      return;
    }
    
    try {
      await deleteContact(id);
      setContacts(contacts.filter(c => c.id !== id));
      if (selectedContact?.id === id) {
        setSelectedContact(null);
      }
      toast({
        title: "Sucesso",
        description: "Mensagem deletada",
      });
    } catch (error) {
      toast({
        title: "Erro",
        description: "Erro ao deletar mensagem",
        variant: "destructive",
      });
    }
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.message.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = !filterUnread || !contact.read;
    
    return matchesSearch && matchesFilter;
  });

  const unreadCount = contacts.filter(c => !c.read).length;

  const handleLogout = () => {
    if (window.confirm('Tem certeza que deseja sair?')) {
      localStorage.removeItem('hngt_admin_auth');
      navigate('/');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#E5E5E5]">
      {/* Header */}
      <header className="border-b border-[#1A1A1A] bg-[#0A0A0A] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-[#E5E5E5] hover:text-[#7C3AED]"
            >
              <ArrowLeft size={20} className="mr-2" />
              Voltar ao Site
            </Button>
            <h1 className="text-2xl font-bold text-[#7C3AED]">Painel Admin</h1>
          </div>
          <div className="flex items-center gap-4">
            <Badge className="bg-[#7C3AED] text-white">
              {unreadCount} não lidas
            </Badge>
            <Button
              onClick={fetchContacts}
              className="bg-[#7C3AED] hover:bg-[#6D28D9]"
            >
              Atualizar
            </Button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-[#2A2A2A] text-[#E5E5E5] hover:bg-[#2A2A2A]"
            >
              <LogOut size={16} className="mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista de Mensagens */}
          <div className="lg:col-span-1">
            <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
              <CardHeader>
                <CardTitle className="text-[#E5E5E5] flex items-center gap-2">
                  <Mail size={20} />
                  Mensagens ({filteredContacts.length})
                </CardTitle>
                <div className="space-y-3 mt-4">
                  <Input
                    type="text"
                    placeholder="Buscar..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-[#0A0A0A] border-[#2A2A2A] text-[#E5E5E5]"
                  />
                  <Button
                    onClick={() => setFilterUnread(!filterUnread)}
                    variant={filterUnread ? "default" : "outline"}
                    className={filterUnread ? "bg-[#7C3AED] w-full" : "w-full border-[#2A2A2A] text-[#E5E5E5]"}
                  >
                    {filterUnread ? 'Mostrando não lidas' : 'Mostrar todas'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[70vh] overflow-y-auto">
                {loading ? (
                  <p className="text-[#6B7280] text-center py-8">Carregando...</p>
                ) : filteredContacts.length === 0 ? (
                  <p className="text-[#6B7280] text-center py-8">Nenhuma mensagem encontrada</p>
                ) : (
                  filteredContacts.map(contact => (
                    <div
                      key={contact.id}
                      onClick={() => setSelectedContact(contact)}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        selectedContact?.id === contact.id
                          ? 'bg-[#7C3AED]/20 border-[#7C3AED]'
                          : 'bg-[#0A0A0A] border-[#2A2A2A] hover:border-[#7C3AED]/50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="font-semibold text-[#E5E5E5]">{contact.name}</h3>
                        {!contact.read && (
                          <Badge className="bg-[#7C3AED] text-white text-xs">Nova</Badge>
                        )}
                      </div>
                      <p className="text-sm text-[#6B7280] mb-2">{contact.email}</p>
                      <p className="text-sm text-[#9CA3AF] truncate">
                        {contact.message.substring(0, 60)}...
                      </p>
                      <p className="text-xs text-[#6B7280] mt-2">
                        {formatDate(contact.created_at)}
                      </p>
                    </div>
                  ))
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detalhes da Mensagem */}
          <div className="lg:col-span-2">
            {selectedContact ? (
              <Card className="bg-[#1A1A1A] border-[#2A2A2A]">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-[#E5E5E5] mb-2">
                        {selectedContact.name}
                      </CardTitle>
                      <p className="text-[#6B7280]">{selectedContact.email}</p>
                      <p className="text-sm text-[#6B7280] mt-1">
                        {formatDate(selectedContact.created_at)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {!selectedContact.read && (
                        <Button
                          onClick={() => handleMarkAsRead(selectedContact.id)}
                          className="bg-green-600 hover:bg-green-700"
                          size="sm"
                        >
                          <Check size={16} className="mr-1" />
                          Marcar como Lida
                        </Button>
                      )}
                      <Button
                        onClick={() => handleDelete(selectedContact.id)}
                        variant="destructive"
                        size="sm"
                      >
                        <Trash2 size={16} className="mr-1" />
                        Deletar
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg p-6">
                    <h4 className="text-sm font-semibold text-[#7C3AED] mb-3">MENSAGEM:</h4>
                    <p className="text-[#E5E5E5] leading-relaxed whitespace-pre-wrap">
                      {selectedContact.message}
                    </p>
                  </div>
                  
                  <div className="mt-6 p-4 bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg">
                    <h4 className="text-sm font-semibold text-[#7C3AED] mb-2">INFORMAÇÕES:</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-[#6B7280]">Status:</span>
                        <span className={selectedContact.read ? 'text-green-500' : 'text-[#7C3AED]'}>
                          {selectedContact.read ? 'Lida' : 'Não lida'}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[#6B7280]">ID:</span>
                        <span className="text-[#E5E5E5] font-mono text-xs">
                          {selectedContact.id}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-[#1A1A1A] border-[#2A2A2A] h-full flex items-center justify-center">
                <CardContent className="text-center py-16">
                  <Mail size={48} className="mx-auto text-[#6B7280] mb-4" />
                  <p className="text-[#6B7280] text-lg">Selecione uma mensagem para visualizar</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admin;